import { Head, Link, useForm } from '@inertiajs/react'
import type { ChangeEvent, FormEvent, ReactNode } from 'react'

interface Props {
  emailField: string
  error?: string
}

// Format phone number as user types: (555) 867-5309
function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '')
  if (digits.length === 0) return ''
  if (digits.length <= 3) return `(${digits}`
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
}

function isEmailInput(value: string): boolean {
  return value.includes('@') || (value.length > 0 && /^[a-zA-Z]/.test(value))
}

function New({ emailField, error }: Props) {
  const { data, setData, post, processing } = useForm({
    passwordless: {
      [emailField]: '',
    },
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post('/users/sign_in')
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    if (isEmailInput(raw)) {
      setData('passwordless', { [emailField]: raw })
    } else {
      const formatted = formatPhoneNumber(raw)
      setData('passwordless', { [emailField]: formatted })
    }
  }

  const inputValue = (data.passwordless as Record<string, string>)?.[emailField] || ''
  const isEmail = isEmailInput(inputValue)

  return (
    <>
      <Head title="Sign in" />

      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h1 className="text-center text-3xl font-bold text-black">
            <Link href="/" className="hover:opacity-80 transition-opacity">
              three.pm
            </Link>
          </h1>
          <h2 className="mt-6 text-center text-2xl text-gray-900">
            Sign in
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="identifier" className="block text-sm font-medium text-gray-700">
                  Phone number or Apple ID email
                </label>
                <div className="mt-1">
                  <input
                    id="identifier"
                    name={`passwordless[${emailField}]`}
                    type={isEmail ? 'email' : 'tel'}
                    autoComplete={isEmail ? 'email' : 'tel'}
                    placeholder="(555) 867-5309 or email@icloud.com"
                    required
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-black focus:border-black sm:text-sm"
                    value={inputValue}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={processing}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black disabled:opacity-50"
                >
                  {processing ? 'Sending...' : 'Send me a login link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}

// Opt out of default layout
New.layout = (page: ReactNode) => page

export default New
